import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import { AuthCard } from './components/AuthCard';
import { validateEmail } from './validation';
import type { TranslationKey } from '@/i18n';

const ForgotPage = () => {
  const { t } = useI18n();
  const { resetPassword, isPending } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<TranslationKey | undefined>();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextError = validateEmail(email);
    setError(nextError);
    if (nextError) return;

    await resetPassword(email.trim());
    notify(t('auth.forgot.sent', { email: email.trim() }), 'success');
    setEmail('');
  };

  return (
    <AuthCard
      title={t('auth.forgot.title')}
      description={t('auth.forgot.text')}
      showTabs={false}
    >
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-field">
          <label htmlFor="forgot-email">{t('auth.email')}</label>
          <input
            id="forgot-email"
            type="email"
            autoComplete="email"
            value={email}
            placeholder={t('auth.emailPlaceholder')}
            aria-invalid={Boolean(error)}
            onChange={(event) => setEmail(event.target.value)}
          />
          {error && <span className="field-error">{t(error)}</span>}
        </div>

        <button type="submit" className="auth-primary" disabled={isPending}>
          {isPending ? t('common.loading') : t('auth.submitForgot')}
        </button>

        <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
          {t('auth.backToLogin')}
        </button>
      </form>
    </AuthCard>
  );
};

export default ForgotPage;
